import {ColumnDef, createColumnHelper} from '@tanstack/react-table'
import {FC, useState, useEffect, useMemo, useRef} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {showSwal} from '../../../../ui-kit'
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Spinner,
  OverlayTrigger,
  Tooltip,
  Alert,
} from 'react-bootstrap'
import {
  createAgreement,
  deleteAgreementContentById,
  exportAgreementDetail,
  getAgreementById,
  getOptionAgreements,
  updateAgreementById,
} from './core/_requests'

import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {Link, useNavigate, useParams} from 'react-router-dom'
import {AgreementDetail} from './core/_models'
import './TradeDataAgreementDetailPage.scss'
import Select, {MultiValue} from 'react-select'
import DataTable from '../../../../ui-kit/DataTable'
import {AgreementChapter} from '../trade-data-agreement-chapter/core/_models'
import {useAuth} from '../../../modules/auth'
import AgreementDetailView from './components/AgreementDetailView'
import ModalAddSources from './components/ModalAddSources'
import {useAbility} from '@casl/react'
import {AbilityContext} from '../../../modules/auth/core/AbilityContext'
import NoPermissionCard from '../../../../_metronic/layout/components/permission/NoPermissionCard'
import {getISO2ByISO3} from '../../../libs/misc'
import {AGREEMENT_STATUS_OPTIONS} from '../../../libs/constant'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'

const ReactSwal = withReactContent(Swal)
const confirmDialog = (title: string) => {
  return ReactSwal.fire({
    title,
    icon: 'question',
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    customClass: {
      title: 'fw-bold',
      cancelButton: 'btn btn-danger btn-sm',
      confirmButton: 'btn btn-success btn-sm',
    },
    padding: '1rem',
  })
}

const columnHelper = createColumnHelper<AgreementChapter>()
const sourceColumnHelper = createColumnHelper<AgreementSource>()

type AgreementSource = {
  id: string // local id (uuid / timestamp)
  alias: string
  link: string
}

const TradeDataAgreementDetailPage: FC<{subject: string}> = ({subject}) => {
  const axiosPrivate = useAxiosPrivate()
  const navigate = useNavigate()
  const {currentUser} = useAuth()

  // const countries = [{ value: 'EUN', label: 'European Union (EUN)' }, ...countryOptions]

  const {idAgreement} = useParams()
  const isDetailMode = Boolean(idAgreement)
  const isAddMode = !idAgreement

  const [isEditMode, setIsEditMode] = useState(false)
  const [isLoadingAgreement, setIsLoadingAgreement] = useState(false)
  const [isLoadingButton, setIsLoadingButton] = useState(false)
  const [isFormValid, setIsFormValid] = useState(false)
  const [agreementType, setAgreementType] = useState([])
  const [agreementNotification, setAgreementNotification] = useState([])
  const [agreementComposition, setAgreementComposition] = useState([])
  const [agreementDocumentType, setAgreementDocumentType] = useState([])
  const [countries, setCountries] = useState<any[]>([])

  const [importFile, setImportFile] = useState<File | null>(null)
  const [importError, setImportError] = useState<string | null>(null)
  const [agreementDetail, setAgreementDetail] = useState<AgreementDetail>({
    acronym: '',
    agreement: '',
    type: [],
    wto_rta_id: '',
    status: '',
    notification: [],
    date_signed: '',
    date_into_force: '',
    date_notification: '',
    date_of_implementation: '',
    end_implementation: '',
    date_inactive: '',
    composition: '',
    region: '',
    parties_wto: '',
    crossregional: '',
    language: '',
    sources: '',
    date_xml_created: '',
    date_xml_edited: '',
    xml_file: '',
    xml_edited_comments: '',
    text_available: '',
    document_type: null,
    parties_escap: '',
    services_schedules: '',
    wits_id: '',
    desta_id: '',
    tota_id: '',
    aptiad_id: '',
    iso: [], //refer to parties
  })
  const [pendingChapters, setPendingChapters] = useState([])

  const [showSourceModal, setShowSourceModal] = useState(false)
  const [sources, setSources] = useState<AgreementSource[]>([])
  const [sourceAlias, setSourceAlias] = useState('')
  const [sourceLink, setSourceLink] = useState('')

  const handleAddSource = () => {
    if (!sourceAlias.trim() || !sourceLink.trim()) return

    setSources((prev) => [
      ...prev,
      {
        id: `${Date.now()}`, // simple unique id
        alias: sourceAlias.trim(),
        link: sourceLink.trim(),
      },
    ])

    // reset input
    setSourceAlias('')
    setSourceLink('')
  }

  const handleDeleteSource = (id: string) => {
    setSources((prev) => prev.filter((item) => item.id !== id))
  }

  const sourceColumns: ColumnDef<AgreementSource, any>[] = useMemo(
    () => [
      sourceColumnHelper.accessor('alias', {
        header: 'Alias',
        cell: (info) => info.getValue() || '-',
        meta: {
          headerClass: 'text-left',
          cellClass: 'text-left fw-semibold',
        },
        enableSorting: true,
        enableGlobalFilter: true,
      }),

      sourceColumnHelper.accessor('link', {
        header: 'Link',
        cell: (info) => {
          const value = info.getValue()
          return value ? (
            <a href={value} target='_blank' rel='noreferrer' className='text-link'>
              {value}
            </a>
          ) : (
            '-'
          )
        },
        meta: {
          headerClass: 'text-left',
          cellClass: 'text-left text-break',
        },
        enableSorting: false,
        enableGlobalFilter: true,
      }),

      sourceColumnHelper.accessor('id', {
        header: '',
        cell: (info) => {
          const id = info.getValue()

          return isEditMode ? (
            <Button
              variant='link'
              className='text-danger p-0'
              onClick={() => handleDeleteSource(id)}
            >
              Delete
            </Button>
          ) : (
            <a href={sources.find((s) => s.id === id)?.link} target='_blank' rel='noreferrer'>
              Open
            </a>
          )
        },
        meta: {
          headerClass: 'text-end',
          cellClass: 'text-end',
        },
        enableSorting: false,
        enableGlobalFilter: false,
      }),
    ],
    [isEditMode, sources]
  )

  // table chapters
  const [dataTable, setDataTable] = useState<AgreementChapter[]>([])
  const [includeZero, setIncludeZero] = useState(false)

  const ability = useAbility(AbilityContext)

  const columns: ColumnDef<AgreementChapter, any>[] = useMemo(
    () =>
      [
        columnHelper.accessor('chapter_name', {
          header: 'Chapter Name',
          cell: (info) => (info.getValue() ? info.getValue() : '-'),
          meta: {
            headerClass: 'text-left',
            cellClass: 'text-left',
          },
          enableSorting: true,
          enableGlobalFilter: true,
        }),
        columnHelper.accessor('chapter_number', {
          header: 'Chapter Number',
          cell: (info) => (info.getValue() ? info.getValue() : '-'),
          meta: {
            headerClass: 'text-left',
            cellClass: 'text-left font-semibold text-dark',
          },
          enableSorting: true,
          enableGlobalFilter: true,
        }),
        columnHelper.accessor('article_name', {
          header: 'Article Name',
          cell: (info) => (info.getValue() ? info.getValue() : '-'),
          meta: {
            headerClass: 'text-left',
            cellClass: 'text-left',
          },
          enableSorting: true,
          enableGlobalFilter: true,
        }),
        columnHelper.accessor('article_number', {
          header: 'Article Number',
          cell: (info) => (info.getValue() ? info.getValue() : '-'),
          meta: {
            headerClass: 'text-left',
            cellClass: 'text-left',
          },
          enableSorting: true,
          enableGlobalFilter: true,
        }),
        columnHelper.accessor('chapter_id', {
          header: 'Chapter Id',
          cell: (info) => (info.getValue() ? info.getValue() : '-'),
          meta: {
            headerClass: 'text-left',
            cellClass: 'text-left',
          },
          enableSorting: true,
          enableGlobalFilter: true,
        }),
        columnHelper.accessor('article_id', {
          header: 'Article Id',
          cell: (info) => (info.getValue() ? info.getValue() : '-'),
          meta: {
            headerClass: 'text-left',
            cellClass: 'text-left',
          },
          enableSorting: true,
          enableGlobalFilter: true,
        }),
        columnHelper.accessor('nwords_new', {
          header: 'N Words',
          cell: (info) => info.getValue() ?? 0,
          meta: {
            headerClass: 'text-left',
            cellClass: 'text-left',
          },
          enableSorting: true,
          enableGlobalFilter: true,
        }),
        (ability.can('update', subject) || ability.can('delete', subject)) &&
          columnHelper.accessor('id', {
            header: '',
            cell: (info) => {
              const id = info.getValue()
              return (
                <div className='d-flex gap-3'>
                  {ability.can('update', subject) ? (
                    <Link to={`/agreement/chapter/${id}/edit`} className='link-warning'>
                      <span className='fa fa-edit'></span>
                    </Link>
                  ) : (
                    ''
                  )}
                  {ability.can('delete', subject) ? (
                    <Link to='#' className='link-danger' onClick={() => handleDeleteContent(id)}>
                      <span className='fa fa-trash'></span>
                    </Link>
                  ) : (
                    ''
                  )}
                </div>
              )
            },
            meta: {
              headerClass: 'font-bold',
              cellClass: 'text-end',
            },
            enableSorting: false,
            enableGlobalFilter: true,
          }),
      ].filter(Boolean) as ColumnDef<AgreementChapter, any>[],
    [includeZero]
  ) // Columns don't depend on state, so empty dependency array is fine here
  // end of table chapters

  const parseSources = (raw?: string): AgreementSource[] => {
    if (!raw) return []

    // legacy case (single link, no |)
    if (!raw.includes('|')) {
      return [
        {
          id: `${Date.now()}_${Math.random()}`,
          alias: raw,
          link: raw,
        },
      ]
    }

    return raw.split(';').map((pair, index) => {
      const [alias, link] = pair.split('|')

      return {
        id: `${Date.now()}_${index}`,
        alias: alias || link,
        link: link,
      }
    })
  }

  const fetchOptionAgreement = async () => {
    try {
      setIsLoadingAgreement(true)

      const optionsRes = await getOptionAgreements(axiosPrivate)
      const resData = optionsRes.data.data
      setAgreementType(resData.type.map((n: string) => ({value: n, label: n})))
      setAgreementNotification(resData.notification.map((n: string) => ({value: n, label: n})))
      setAgreementComposition(resData.composition)
      setAgreementDocumentType(resData.document_type)
      setCountries([
        {value: 'EUN', label: 'European Union (EUN)', region_un: ['Europe'], escap: false},
        {value: 'TWN', label: 'Other Asia (TWN)', region_un: ['Asia Pacific'], escap: false},
        ...resData.parties, // ← backend must return this
      ])

      if (isDetailMode) {
        const detailRes = await getAgreementById(axiosPrivate, {idAgreement})
        const detailResdata = detailRes.data.data
        if (detailResdata) {
          const agreementDetailType = detailResdata.type ? detailResdata.type.split(';') : []

          const agreementDetailNotification = detailResdata.notification
            ? detailResdata.notification.split(';')
            : []

          const agreementDetailParties = detailResdata.iso ? detailResdata.iso.split(';') : []
          const agreementDetailSources = parseSources(detailResdata.sources)

          setAgreementDetail((prev) => ({
            ...prev,
            ...detailResdata,
            type: agreementDetailType,
            notification: agreementDetailNotification,
            iso: agreementDetailParties,
            sources: agreementDetailSources,
          }))
          setSources(agreementDetailSources)
          setDataTable(detailResdata.chapters)
          setPendingChapters(detailResdata.pending_chapters)
          // should be add the optionPartiesHere
        }
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoadingAgreement(false)
    }
  }
  const isEditDisabled = pendingChapters.length > 0

  useEffect(() => {
    fetchOptionAgreement()
  }, [])

  // Add or Edit
  const handleAgreementChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const {name, value, multiple, selectedOptions} = e.target as HTMLSelectElement

    if (multiple) {
      const values = Array.from(selectedOptions, (option) => option.value)
      setAgreementDetail((prev) => ({
        ...prev,
        [name]: values,
      }))
    } else {
      setAgreementDetail((prev) => ({
        ...prev,
        [name]: value,
      }))
    }
  }

  const handlePartiesChange = (selected: MultiValue<any>) => {
    const selectedValues = selected?.map((s) => s.value) || []

    // 🔥 If nothing selected → reset derived fields
    if (selectedValues.length === 0) {
      setAgreementDetail((prev) => ({
        ...prev,
        iso: [],
        region: '',
        crossregional: '',
        parties_escap: '',
      }))
      return
    }

    const selectedCountries = countries.filter((c) => selectedValues.includes(c.value))

    const regionSet = new Set<string>()
    selectedCountries.forEach((c) => {
      c.region_un?.forEach((r: string) => regionSet.add(r))
    })

    const regionArray = Array.from(regionSet)

    const crossregional = regionArray.length > 1 ? 'Yes' : 'No'

    const partiesEscap = selectedCountries.some((c) => c.escap) ? 'Yes' : 'No'

    setAgreementDetail((prev) => ({
      ...prev,
      iso: selectedValues,
      region: regionArray.join('; '),
      crossregional,
      parties_escap: partiesEscap,
    }))
  }

  const validateRequiredFields = () => {
    const requiredFields = ['agreement', 'acronym', 'type', 'status', 'parties_wto', 'composition']

    const isValid = requiredFields.every((key) => {
      const val = (agreementDetail as any)[key]
      return val !== undefined && val !== null && String(val).trim() !== ''
    })

    setIsFormValid(isValid)
  }

  useEffect(() => {
    validateRequiredFields()
  }, [agreementDetail])

  useEffect(() => {
    const MAX_FILE_SIZE_MB = 5
    const maxFileSize = MAX_FILE_SIZE_MB * 1024 * 1024

    if (importFile && importFile.size > maxFileSize) {
      setImportError(`File size exceeds ${MAX_FILE_SIZE_MB}MB. Please upload a smaller file.`)
      setIsFormValid(false)
    } else {
      validateRequiredFields()
    }
  }, [importFile])

  const serializeSources = (sources: AgreementSource[], legacySource?: string) => {
    // CASE 1: new multi-source UI
    if (sources.length > 0) {
      return sources
        .filter((s) => s.link)
        .map((s) => `${s.alias || s.link}|${s.link}`)
        .join(';')
    }

    // CASE 2: legacy string → upgrade format
    if (legacySource && legacySource.trim() !== '') {
      const link = legacySource.trim()
      return `${link}|${link}`
    }

    // CASE 3: empty
    return ''
  }

  const handleSubmit = () => {
    const formData = new FormData()

    // Dynamically append all agreementDetail fields
    Object.entries(agreementDetail).forEach(([key, value]) => {
      // Skip specific keys
      if (
        ['xml_file', 'id', 'is_new', 'chapters', 'pending_chapters', 'parties', 'sources'].includes(
          key
        )
      )
        return

      if (value !== null && value !== undefined) {
        let formattedValue = value

        if (key === 'date_signed' && value === '') {
          formattedValue = 'XXXX-XX-XX'
        }

        if ((key === 'notification' || key === 'type' || key === 'iso') && Array.isArray(value)) {
          formattedValue = value.join(';')
        }

        if (key === 'crossregional' || key === 'parties_escap') {
          formattedValue = String(value).toLowerCase()
        }

        formData.append(key, String(formattedValue)) // Convert value to string
      }
    })

    const sourcesString = serializeSources(sources, agreementDetail.sources)
    formData.append('sources', sourcesString)

    // Append file if available
    if (importFile) {
      formData.append('file', importFile)
    }

    // BRA, CHL, EGY, ISR, KOR, MEX, PAR, PRY, PER, PHL, TUN, TUR, URY, SRB, EUN, BGD
    // West Asia; South America; Africa; Middle East; East Asia; North America; Europe
    // Europe; Africa & Middle East; Latin America & Caribbean; Asia Pacific

    showSwal({
      title: `Are you sure you want to ${isEditMode ? 'update' : 'create'} this agreement?`,
      icon: 'question',
    }).then((result) => {
      if (!result.isConfirmed) return // exit if user cancels

      setIsLoadingButton(true)

      if (isEditMode) {
        updateAgreementById(axiosPrivate, idAgreement, formData)
          .then((res) => {
            if (res.status === 200) {
              showSwal('Data updated', undefined, 'success').then(() => {
                window.location.reload()
              })
            }
          })
          .catch((err) => {
            console.error(err)
            showSwal('Error', 'Failed to update data', 'error')
          })
          .finally(() => {
            setIsLoadingButton(false)
          })
      } else {
        createAgreement(axiosPrivate, formData)
          .then((res) => {
            if (res.status === 200) {
              showSwal('Data created', undefined, 'success').then(() => {
                navigate('/agreement')
              })
            }
          })
          .catch((err) => {
            console.error(err)
            showSwal('Error', 'Failed to create data', 'error')
          })
          .finally(() => {
            setIsLoadingButton(false)
          })
      }
    })
  }

  const handleCancel = () => {
    showSwal({
      title: 'Are you sure you want to cancel?',
      icon: 'question',
    }).then((result) => {
      if (result.isConfirmed) {
        if (isAddMode) {
          navigate('/agreement')
        } else {
          window.location.reload()
        }
      }
    })
  }
  // End of Add or Edit
  // Contents
  const tableHelpersRef = useRef<any>(null)
  const handleDeleteContent = (id: any) => {
    showSwal({
      title: 'Are you sure you want to delete this content?',
      icon: 'question',
    }).then((result) => {
      if (result.isConfirmed) {
        deleteAgreementContentById(axiosPrivate, id)
          .then((res) => {
            if (res.status === 200) {
              tableHelpersRef.current?.skipPageReset()
              setDataTable((prev) => prev.filter((row) => row.id !== id))
              showSwal('Data deleted', undefined, 'success')
            }
          })
          .catch((err) => {
            console.error(err)
            showSwal('Error', 'Failed to delete data', 'error')
          })
          .finally(() => {
            setIsLoadingButton(false)
          })
      }
    })
  }

  // Export Detail
  const [isExporting, setIsExporting] = useState(false)
  const [exportProgress, setExportProgress] = useState(0)
  const [exportMessage, setExportMessage] = useState('Exporting data')
  const handleDownloadAgreements = () => {
    setIsExporting(true)

    const body = {
      rta_id: idAgreement,
    }

    exportAgreementDetail(axiosPrivate, body)
      .then((res) => {
        const blob = new Blob([res.data], {type: 'text/csv'})

        const url = window.URL.createObjectURL(blob)

        const link = document.createElement('a')
        link.href = url
        link.download = `agreement_${idAgreement}_export.csv`

        document.body.appendChild(link)
        link.click()

        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        showSwal('Export Complete', undefined, 'success')
      })
      .catch((e) => {
        const resError = e.response
        showSwal('Error', resError?.data?.message || 'Export failed', 'error')
      })
      .finally(() => {
        setIsExporting(false)
      })
  }
  // End of Export Detail

  const canApprove = ability.can('approve', 'PERM_APPROVE_AGREEMENT')

  return (
    <>
      {isExporting && (
        <div className='sync-overlay'>
          <div className='sync-box'>
            <h4>{exportMessage}</h4>
            <p>
              This export process may take a few minutes, during which the system will be
              temporarily unavailable.
            </p>
          </div>
        </div>
      )}
      <PageTitle
        breadcrumbs={[
          {
            title: 'Home',
            path: '/',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
          {
            title: 'Trade Data Agreement',
            path: 'agreement',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
        ]}
      >
        {isAddMode ? 'Add' : isEditMode ? 'Edit' : agreementDetail.agreement}
      </PageTitle>
      <div className='TradeDataAgreementDetailPage'>
        {canApprove && !isAddMode && pendingChapters && pendingChapters.length > 0 && (
          <div className='alert-no-result-data'>
            <div className='alert alert-warning d-flex align-items-center justify-content-between p-5'>
              <div className='alert-content d-flex align-items-start'>
                <span className='svg-icon svg-icon-2hx svg-icon-warning me-3'>
                  <i className='fa-solid fa-triangle-exclamation'></i>
                </span>
                <div className='d-flex flex-column'>
                  <h5 className='mb-1'>Review Needed</h5>
                  <span>There are chapters that require review or approval:</span>

                  <ul className='mt-2 mb-0'>
                    {pendingChapters.map((chapter: any, index) => (
                      <li key={index}>
                        <a className='text-link' href={`/agreement/review-chapter/${chapter.id}`}>
                          {chapter.article_name}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        <Row className='mb-5'>
          <Col>
            <Card>
              <Card.Header className='card-header-stretch'>
                <Card.Title className='w-100'>
                  <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                    <div className='title d-flex align-items-center'>
                      <span className='fa fa-file-alt px-2'></span>
                      <h3 className='text-gray-700'>
                        {isDetailMode ? `Detail ${agreementDetail.agreement}` : 'Add New Agreement'}
                      </h3>
                    </div>
                    {ability.can('update', subject) && isDetailMode && !isEditMode ? (
                      <OverlayTrigger
                        placement='top'
                        overlay={
                          isEditDisabled ? (
                            <Tooltip id='edit-disabled-tooltip'>
                              Cannot edit while there are chapters pending review
                            </Tooltip>
                          ) : (
                            <></>
                          )
                        }
                      >
                        <span className='d-inline-block'>
                          <Button
                            className='btn btn-primary d-flex gap-4 align-items-center'
                            onClick={() => setIsEditMode(true)}
                            disabled={isEditDisabled}
                            style={{pointerEvents: isEditDisabled ? 'none' : 'auto'}} // important
                          >
                            <span className='fa fa-pencil'></span>
                            Edit
                          </Button>
                        </span>
                      </OverlayTrigger>
                    ) : (
                      ''
                    )}
                  </div>
                </Card.Title>
              </Card.Header>
              <Card.Body>
                {!isLoadingAgreement ? (
                  <>
                    {isAddMode || isEditMode ? (
                      <Form className='form-agreement'>
                        <div className='row'>
                          <div className='col-12'>
                            <h3>General Information</h3>
                          </div>
                          <div className='col-md-8'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              <span className='required'>Name</span>
                            </Form.Label>
                            <Form.Control
                              placeholder='Enter full agreement name'
                              type='text'
                              name='agreement'
                              value={agreementDetail.agreement}
                              onChange={handleAgreementChange}
                            />
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              <span className='required'>Short Name</span>
                            </Form.Label>
                            <Form.Control
                              type='text'
                              placeholder='Enter short name'
                              name='acronym'
                              value={agreementDetail.acronym}
                              onChange={handleAgreementChange}
                            />
                          </div>
                          {isEditMode && (
                            <div className='col-md-4'>
                              <Form.Label className='text-primary me-0' column>
                                Treaty Identifier TINA
                              </Form.Label>
                              <Form.Control type='text' value={idAgreement || ''} readOnly />
                            </div>
                          )}
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0' column>
                              <span className='required'>Type</span>
                            </Form.Label>
                            <Select
                              isMulti
                              styles={{
                                valueContainer: (baseStyles) => ({
                                  ...baseStyles,
                                  height: '3.27rem',
                                  overflowY: 'auto',
                                }),
                              }}
                              name='type'
                              placeholder='Select type(s)'
                              options={agreementType} // now [{ value, label }, ...]
                              value={
                                agreementDetail.type?.map((n: string) => ({
                                  value: n,
                                  label: n,
                                })) || []
                              }
                              onChange={(selected) => {
                                setAgreementDetail((prev) => ({
                                  ...prev,
                                  type: selected.map((s) => s.value), // store only the string array
                                }))
                              }}
                            />
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0' column>
                              <span className='required'>Status</span>
                            </Form.Label>
                            <Form.Select
                              name='status'
                              value={agreementDetail.status}
                              onChange={handleAgreementChange}
                            >
                              <option value='' disabled>
                                Select status
                              </option>
                              {AGREEMENT_STATUS_OPTIONS.map((item) => (
                                <option key={item.value} value={item.label}>
                                  {item.label}
                                </option>
                              ))}
                            </Form.Select>
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0' column>
                              WTO RTA Id
                            </Form.Label>
                            <Form.Control
                              type='text'
                              placeholder='Enter WTO RTA Id'
                              name='wto_rta_id'
                              value={agreementDetail.wto_rta_id}
                              onChange={handleAgreementChange}
                            />
                            <a
                              href='https://rtais.wto.org/UI/PublicAllRTAList.aspx'
                              target='_blank'
                              rel='noreferrer'
                              className='text-link'
                            >
                              check the WTO database
                            </a>
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0' column>
                              Notification
                            </Form.Label>
                            <Select
                              isMulti
                              name='notification'
                              styles={{
                                valueContainer: (baseStyles) => ({
                                  ...baseStyles,
                                  height: '3.27rem',
                                  overflowY: 'auto',
                                }),
                              }}
                              placeholder='Select notification(s)'
                              options={agreementNotification} // now [{ value, label }, ...]
                              value={
                                agreementDetail.notification?.map((n: string) => ({
                                  value: n,
                                  label: n,
                                })) || []
                              }
                              onChange={(selected) => {
                                setAgreementDetail((prev) => ({
                                  ...prev,
                                  notification: selected.map((s) => s.value), // store only the string array
                                }))
                              }}
                            />
                          </div>
                        </div>
                        <hr />
                        <div className='row'>
                          <div className='col-12'>
                            <h3>Date Information</h3>
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Date Signed
                            </Form.Label>
                            <Form.Control
                              type='date'
                              name='date_signed'
                              value={agreementDetail.date_signed}
                              onChange={handleAgreementChange}
                            />
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Date Into Force
                            </Form.Label>
                            <Form.Control
                              type='date'
                              name='date_into_force'
                              value={agreementDetail.date_into_force}
                              onChange={handleAgreementChange}
                            />
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Date Notification
                            </Form.Label>
                            <Form.Control
                              type='date'
                              name='date_notification'
                              value={agreementDetail.date_notification}
                              onChange={handleAgreementChange}
                            />
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Date of Implementation
                            </Form.Label>
                            <Form.Control
                              type='date'
                              name='date_of_implementation'
                              value={agreementDetail.date_of_implementation}
                              onChange={handleAgreementChange}
                            />
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              End Implementation
                            </Form.Label>
                            <Form.Control
                              type='date'
                              name='end_implementation'
                              value={agreementDetail.end_implementation}
                              onChange={handleAgreementChange}
                            />
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Date Inactive
                            </Form.Label>
                            <Form.Control
                              type='date'
                              name='date_inactive'
                              value={agreementDetail.date_inactive}
                              onChange={handleAgreementChange}
                            />
                          </div>
                        </div>
                        <hr />
                        <div className='row'>
                          <div className='col-12'>
                            <h3>Participation & Scope</h3>
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0' column>
                              Parties
                            </Form.Label>
                            <Select
                              isMulti
                              name='notification'
                              placeholder='Select country(s)'
                              options={countries}
                              value={
                                agreementDetail.iso
                                  ?.map((iso) => countries.find((option) => option.value === iso))
                                  .filter(Boolean) || []
                              }
                              onChange={handlePartiesChange}
                              styles={{
                                valueContainer: (base) => ({
                                  ...base,
                                  height: '3.2rem',
                                  overflowY: 'auto',
                                }),
                              }}
                            />
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              <span className='required'>Composition</span>
                            </Form.Label>
                            <Form.Select
                              name='composition'
                              value={agreementDetail.composition}
                              onChange={handleAgreementChange}
                            >
                              <option value='' disabled>
                                Select composition
                              </option>
                              {agreementComposition.map((item) => (
                                <option key={item} value={item}>
                                  {item}
                                </option>
                              ))}
                            </Form.Select>
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              <span className='required'>Parties WTO</span>
                            </Form.Label>
                            <Form.Select
                              name='parties_wto'
                              value={agreementDetail.parties_wto}
                              onChange={handleAgreementChange}
                            >
                              <option value='' disabled>
                                Select parties WTO
                              </option>
                              <option value='yes'>Yes</option>
                              <option value='no'>No</option>
                            </Form.Select>
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Region
                            </Form.Label>
                            <textarea
                              className='form-control mb-3'
                              rows={2}
                              data-kt-element='input'
                              placeholder='Please select parties'
                              name='region'
                              value={agreementDetail.region}
                              disabled
                            ></textarea>
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Crossregional
                            </Form.Label>
                            <Form.Control
                              type='text'
                              className='text-capitalize'
                              name='crossregional'
                              value={agreementDetail.crossregional}
                              placeholder='Please select parties'
                              disabled
                            />
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Parties ESCAP
                            </Form.Label>
                            <Form.Control
                              className='text-capitalize'
                              type='text'
                              name='parties_escap'
                              value={agreementDetail.parties_escap}
                              placeholder='Please select parties'
                              disabled
                            />
                          </div>
                        </div>
                        <hr />
                        <div className='row'>
                          <div className='col-12'>
                            <h3>Others</h3>
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Language
                            </Form.Label>
                            <Form.Control
                              type='text'
                              name='language'
                              placeholder='Enter language code (e.g. EN)'
                              value={agreementDetail.language}
                              onChange={handleAgreementChange}
                            />
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Service Schedule
                            </Form.Label>
                            <Form.Select
                              name='services_schedules'
                              value={agreementDetail.services_schedules}
                              onChange={handleAgreementChange}
                            >
                              <option value='' disabled>
                                Select service schedule
                              </option>
                              {agreementDetail.services_schedules && (
                                <option value=''>-- Clear selection --</option>
                              )}
                              <option value='yes'>Yes</option>
                              <option value='no'>No</option>
                            </Form.Select>
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              WITS Id
                            </Form.Label>
                            <Form.Control
                              type='text'
                              name='wits_id'
                              placeholder='Enter WITS Id'
                              value={agreementDetail.wits_id}
                              onChange={handleAgreementChange}
                            />
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Desta Id
                            </Form.Label>
                            <Form.Control
                              type='text'
                              name='desta_id'
                              placeholder='Enter Desta Id'
                              value={agreementDetail.desta_id}
                              onChange={handleAgreementChange}
                            />
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Tota Id
                            </Form.Label>
                            <Form.Control
                              type='text'
                              name='tota_id'
                              placeholder='Enter Tota Id'
                              value={agreementDetail.tota_id}
                              onChange={handleAgreementChange}
                            />
                          </div>
                          <div className='col-md-4'>
                            <Form.Label className='text-primary me-0 ps-0' column>
                              Aptiad Id
                            </Form.Label>
                            <Form.Control
                              type='text'
                              name='aptiad_id'
                              placeholder='Enter Aptiad Id'
                              value={agreementDetail.aptiad_id}
                              onChange={handleAgreementChange}
                            />
                          </div>
                        </div>
                        <hr />
                        <div className='row align-items-end'>
                          <div className='col-lg-12 d-flex align-items-center justify-content-between'>
                            <h3>Sources</h3>
                            <Button onClick={() => setShowSourceModal(true)}>Add Source</Button>
                          </div>
                          <div className='col-lg-12'>
                            <DataTable<AgreementSource>
                              data={sources}
                              columns={sourceColumns}
                              enablePagination={false}
                              enableSorting={true}
                              enableGlobalFilter={false}
                              className='my-custom-table-wrapper'
                            />
                          </div>
                        </div>
                        <div className='row pt-5'>
                          <div className='col-lg-12 d-flex justify-content-end gap-3'>
                            <Button variant='default' className='btn-cancel' onClick={handleCancel}>
                              Cancel
                            </Button>
                            <Button
                              variant='primary'
                              onClick={handleSubmit}
                              disabled={isLoadingButton || !isFormValid}
                            >
                              {isLoadingButton ? (
                                <>
                                  <Spinner animation='border' size='sm' className='me-2' />
                                  Submit
                                </>
                              ) : (
                                'Submit'
                              )}
                            </Button>
                          </div>
                        </div>
                      </Form>
                    ) : (
                      <AgreementDetailView agreementDetail={agreementDetail} />
                    )}
                  </>
                ) : (
                  <div
                    className='d-flex align-items-center justify-content-center'
                    style={{height: '50vh'}}
                  >
                    <Spinner animation='border' role='status' variant='primary' className='me-3' />
                    <div className='loading-text'>
                      Loading
                      <span className='dot'>.</span>
                      <span className='dot'>.</span>
                      <span className='dot'>.</span>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
        {idAgreement && !isEditMode ? (
          <Row>
            <Col>
              <Card>
                <Card.Header className='card-header-stretch'>
                  <Card.Title className='w-100'>
                    <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                      <div className='title d-flex align-items-center'>
                        <span className='fa fa-file-alt px-2'></span>
                        <h3 className='text-gray-700'>List Content</h3>
                      </div>
                      <div className='header-action'>
                        <Button
                          className='btn btn-primary btn-outline'
                          onClick={() => {
                            handleDownloadAgreements()
                          }}
                        >
                          <span className='fa fa-download px-2'></span>
                          Export CSV
                        </Button>
                        <Link
                          to={`/agreement/${idAgreement}/chapter/add`}
                          className='btn btn-primary d-flex gap-4 align-items-center'
                        >
                          <span className='fa fa-plus'></span>
                          Add
                        </Link>
                        {dataTable.length > 1 && (
                          <OverlayTrigger
                            placement='top'
                            overlay={
                              isEditDisabled ? (
                                <Tooltip id='edit-disabled-tooltip'>
                                  Cannot edit while there are chapters pending review
                                </Tooltip>
                              ) : (
                                <></>
                              )
                            }
                          >
                            <span className='d-inline-block'>
                              <Button
                                onClick={() =>
                                  navigate(`/agreement/${idAgreement}/chapter/edit-order`)
                                }
                                variant='outline-primary'
                                disabled={isEditDisabled}
                                style={{pointerEvents: isEditDisabled ? 'none' : 'auto'}} // important
                              >
                                Edit Order
                              </Button>
                            </span>
                          </OverlayTrigger>
                        )}
                      </div>
                    </div>
                  </Card.Title>
                </Card.Header>
                <Card.Body>
                  <DataTable<AgreementChapter>
                    data={dataTable}
                    columns={columns}
                    enablePagination={true}
                    enableSorting={true}
                    enableGlobalFilter={true}
                    globalFilterPlaceholder='Search Article Name'
                    defaultPageSize={10}
                    pageSizes={[5, 10, 20, 50, 100]}
                    className='my-custom-table-wrapper'
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        ) : (
          // <NoPermissionCard />
          ''
        )}
      </div>
      <ModalAddSources
        show={showSourceModal}
        onHide={() => setShowSourceModal(false)}
        onSubmit={() => {
          handleAddSource()
          setShowSourceModal(false)
        }}
        alias={sourceAlias}
        link={sourceLink}
        onAliasChange={setSourceAlias}
        onLinkChange={setSourceLink}
      />
    </>
  )
}

export default TradeDataAgreementDetailPage
